import { useEffect, useMemo, useRef } from 'react';

type BoundedRetryOptions = {
  action: () => void;
  maxAttempts: number;
  baseDelayMs: number;
  capDelayMs: number;
  /** Invoked once per budget cycle when schedule() finds the budget spent */
  onExhausted?: () => void;
};

export type BoundedRetry = {
  /** Arm the next retry unless one is pending or the budget is spent */
  schedule: () => void;
  /** Drop the pending retry; the attempt budget keeps its progress */
  cancel: () => void;
  /** Drop the pending retry and restore the full attempt budget */
  reset: () => void;
};

// Retries an action on a timer with exponential backoff (base, 2×, 4×…,
// capped), giving up after maxAttempts. Use it when a failure should trigger a
// few spaced-out retries instead of a tight loop — e.g. recovering a crashed
// process. The hook can't observe success: once scheduled, retries keep
// re-arming themselves until you cancel(), reset(), or the budget runs out
export function useBoundedRetry(options: BoundedRetryOptions): BoundedRetry {
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  const attemptsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const generationRef = useRef(0);
  const exhaustionNotifiedRef = useRef(false);

  const retry = useMemo<BoundedRetry>(() => {
    const cancel = () => {
      generationRef.current += 1;
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
    };

    const schedule = () => {
      if (timerRef.current !== undefined) return;

      const { maxAttempts, baseDelayMs, capDelayMs } = optionsRef.current;
      if (attemptsRef.current >= maxAttempts) {
        if (!exhaustionNotifiedRef.current) {
          exhaustionNotifiedRef.current = true;
          optionsRef.current.onExhausted?.();
        }
        return;
      }

      attemptsRef.current += 1;
      const generation = generationRef.current;
      const delayMs = Math.min(
        baseDelayMs * 2 ** (attemptsRef.current - 1),
        capDelayMs
      );

      timerRef.current = setTimeout(() => {
        timerRef.current = undefined;
        if (generationRef.current !== generation) return;
        optionsRef.current.action();
        schedule();
      }, delayMs);
    };

    return {
      schedule,
      cancel,
      reset: () => {
        cancel();
        attemptsRef.current = 0;
        exhaustionNotifiedRef.current = false;
      },
    };
  }, []);

  useEffect(() => () => retry.cancel(), [retry]);

  return retry;
}
